import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Home as HomeIcon, Hammer, Shield, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';

const IMAGES = {
  home1: "https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/nngvgtcc_11.webp",
  home2: "https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/b4t93wnm_29.webp",
  home3: "https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/ihn4zm9m_Gateway_Model_Living_Room_2_o5lvek.webp",
  home4: "https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/g19s2mu9_Gateway_Model_Bathroom_Bedroom_etqakp.webp",
  bgSkyline: "https://images.unsplash.com/photo-1760385737118-50128d74a952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjaXR5JTIwc2t5bGluZSUyMGJ1aWxkaW5ncyUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3NzI0ODY0OTh8MA&ixlib=rb-4.1.0&q=85",
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const services = [
  {
    icon: HomeIcon,
    title: 'Multifamily',
    description: 'Custom homes, remodeling, and renovations that transform living spaces.',
  },
  {
    icon: Building2,
    title: 'Commercial',
    description: 'Office buildings, retail spaces, and industrial facilities built to last.',
  },
  {
    icon: Hammer,
    title: 'Remodeling',
    description: 'Kitchen, bathroom, and whole-home upgrades with expert craftsmanship.',
  },
];

const values = [
  {
    icon: Shield,
    title: 'Quality First',
    description: 'We never compromise on materials or workmanship.',
  },
  {
    icon: Clock,
    title: 'On Time',
    description: 'Projects delivered on schedule, every time.',
  },
  {
    icon: Users,
    title: 'Client Focused',
    description: 'Your vision drives every decision we make.',
  },
];

export const Home = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden" data-testid="hero-section">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.bgSkyline} 
            alt="City skyline" 
            className="w-full h-full object-cover"
          />
          {/* Dark gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-ktm-navy via-ktm-navy/95 to-ktm-navy/70" />
          {/* Animated geometric shapes */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div 
              className="absolute top-20 right-20 w-96 h-96 border-2 border-ktm-red/20"
              animate={{ rotate: 360 }}
              transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            />
            <motion.div 
              className="absolute bottom-40 right-40 w-64 h-64 border-2 border-white/10"
              animate={{ rotate: -360 }}
              transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
            />
            <motion.div 
              className="absolute top-1/2 right-1/4 w-32 h-32 bg-ktm-red/10"
              animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
              transition={{ duration: 4, repeat: Infinity }}
            />
          </div>
          {/* Grid pattern overlay */}
          <div 
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-56 md:pt-60 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[70vh]">
            {/* Left: Content */}
            <motion.div 
              className="space-y-8"
              initial="initial"
              animate="animate"
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp}>
                <span className="inline-block bg-ktm-red text-white px-4 py-2 text-sm font-bold uppercase tracking-wider mb-4">
                  California &amp; Nevada&apos;s Trusted Builder
                </span>
              </motion.div>
              <motion.h1 
                variants={fadeInUp}
                className="font-chivo font-black text-4xl sm:text-5xl lg:text-7xl text-white leading-tight"
              >
                Building Dreams<br />
                <span className="text-ktm-red">Into Reality</span>
              </motion.h1>
              <motion.p 
                variants={fadeInUp}
                className="text-slate-300 text-lg leading-relaxed max-w-lg"
              >
                KTM Construction delivers exceptional multifamily and commercial construction 
                services in California and Nevada. From new builds to complete renovations, 
                we bring your vision to life with precision and integrity.
              </motion.p>
              <motion.div 
                variants={fadeInUp}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link to="/contact?quote=true">
                  <Button 
                    className="bg-ktm-red text-white h-14 px-8 rounded-none font-bold uppercase tracking-wider hover:bg-ktm-red/90 transition-all hover:scale-105 w-full sm:w-auto"
                    data-testid="hero-quote-btn"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/gallery">
                  <Button 
                    variant="outline"
                    className="bg-transparent text-white border-2 border-white h-14 px-8 rounded-none font-bold uppercase tracking-wider hover:bg-white/10 w-full sm:w-auto"
                    data-testid="hero-gallery-btn"
                  >
                    View Our Work
                  </Button>
                </Link>
              </motion.div>
              
              {/* Quick Stats */}
              <motion.div 
                variants={fadeInUp}
                className="pt-8 border-t border-white/20"
              >
              </motion.div>
            </motion.div>

            {/* Right: Featured Project Cards */}
            <motion.div 
              className="relative hidden lg:block"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="relative grid grid-cols-2 gap-3">
                <motion.div 
                  className="relative z-10 overflow-hidden border-4 border-white/20 shadow-2xl"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={IMAGES.home1} 
                    alt="Featured construction project" 
                    className="w-full aspect-square object-cover"
                  />
                </motion.div>

                <motion.div 
                  className="relative z-10 overflow-hidden border-4 border-white/20 shadow-2xl"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={IMAGES.home3} 
                    alt="Featured construction project" 
                    className="w-full aspect-square object-cover"
                  />
                </motion.div>

                <motion.div 
                  className="relative z-10 overflow-hidden border-4 border-white/20 shadow-2xl"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={IMAGES.home2} 
                    alt="Featured construction project" 
                    className="w-full aspect-square object-cover"
                  />
                </motion.div>

                <motion.div 
                  className="relative z-10 overflow-hidden border-4 border-white/20 shadow-2xl"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={IMAGES.home4} 
                    alt="Featured construction project" 
                    className="w-full aspect-square object-cover"
                  />
                </motion.div>
                
                {/* Decorative elements */}
                <div className="absolute -z-10 top-10 right-10 w-full h-full border-2 border-ktm-red/30" />
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-ktm-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-ktm-red font-bold uppercase tracking-wider text-sm">
              What We Do
            </span>
            <h2 className="font-chivo font-black text-3xl md:text-4xl text-ktm-navy mt-4">
              Our Services
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="bg-white border border-ktm-border p-8 group hover:border-ktm-red transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`service-card-${service.title.toLowerCase()}`}
              >
                <div className="w-16 h-16 bg-ktm-navy flex items-center justify-center mb-6 group-hover:bg-ktm-red transition-colors">
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-chivo font-bold text-xl text-ktm-navy mb-4">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  {service.description}
                </p>
                <Link 
                  to="/services" 
                  className="inline-flex items-center text-ktm-navy font-semibold hover:text-ktm-red transition-colors"
                >
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-24 bg-white" data-testid="featured-projects-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div>
              <span className="text-ktm-red font-bold uppercase tracking-wider text-sm">
                Our Portfolio
              </span>
              <h2 className="font-chivo font-black text-3xl md:text-4xl text-ktm-navy mt-4">
                Featured Projects
              </h2>
            </div>
            <Link to="/gallery">
              <Button 
                variant="outline"
                className="mt-6 md:mt-0 border-2 border-ktm-navy text-ktm-navy rounded-none font-bold uppercase tracking-wider hover:bg-ktm-navy hover:text-white"
                data-testid="view-all-projects-btn"
              >
                View All Projects
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[IMAGES.home1, IMAGES.home3, IMAGES.home2].map((img, index) => (
              <motion.div
                key={index}
                className="relative aspect-[4/3] overflow-hidden group cursor-pointer"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <img 
                  src={img} 
                  alt={`Project ${index + 1}`} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gateway Project Showcase */}
      <section className="py-24 bg-ktm-navy text-white overflow-hidden" data-testid="gateway-showcase-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="grid lg:grid-cols-12 gap-8 items-start mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="lg:col-span-7">
              <span className="text-ktm-red font-bold uppercase tracking-wider text-sm">
                Signature Project
              </span>
              <h2 className="font-chivo font-black text-3xl md:text-5xl mt-3 leading-tight">
                The Gateway<br />
                <span className="text-ktm-red">Transformation</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-slate-300 text-base md:text-lg leading-relaxed">
                A full-property revival — from dated oak kitchens to a fully reimagined exterior with cedar accents and lush landscaping. Every unit refreshed, every detail considered. See the story unfold below.
              </p>
            </div>
          </motion.div>

          {/* Asymmetric photo grid */}
          <div className="grid grid-cols-12 gap-3 md:gap-4">
            {/* Big exterior */}
            <motion.div 
              className="col-span-12 md:col-span-8 row-span-2 relative overflow-hidden border-4 border-white/10 group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/wza04ly1_Gateway_Exterior_2_fcnfwu.webp"
                alt="Gateway exterior"
                className="w-full h-full aspect-[4/3] md:aspect-auto object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute top-3 left-3 bg-ktm-red px-3 py-1">
                <span className="text-white text-xs font-bold uppercase tracking-wider">After</span>
              </div>
            </motion.div>

            {/* Before kitchen */}
            <motion.div 
              className="col-span-6 md:col-span-4 relative overflow-hidden border-4 border-white/10 group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/y130o8s7_Gateway%20kitchen%20before.jpeg"
                alt="Gateway kitchen before"
                className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute top-3 left-3 bg-slate-700/90 px-3 py-1">
                <span className="text-white text-xs font-bold uppercase tracking-wider">Before</span>
              </div>
            </motion.div>

            {/* After kitchen */}
            <motion.div 
              className="col-span-6 md:col-span-4 relative overflow-hidden border-4 border-white/10 group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/3nqmcil5_Gateway_Model_Kitchen_2_nlnvaq.webp"
                alt="Gateway model kitchen"
                className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute top-3 left-3 bg-ktm-red px-3 py-1">
                <span className="text-white text-xs font-bold uppercase tracking-wider">After</span>
              </div>
            </motion.div>

            {/* Living room */}
            <motion.div 
              className="col-span-6 md:col-span-6 relative overflow-hidden border-4 border-white/10 group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/ihn4zm9m_Gateway_Model_Living_Room_2_o5lvek.webp"
                alt="Gateway living room"
                className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </motion.div>

            {/* Bathroom/bedroom */}
            <motion.div 
              className="col-span-6 md:col-span-6 relative overflow-hidden border-4 border-white/10 group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/g19s2mu9_Gateway_Model_Bathroom_Bedroom_etqakp.webp"
                alt="Gateway bathroom and bedroom"
                className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </motion.div>
          </div>

          <motion.div 
            className="mt-12 flex flex-col md:flex-row items-start md:items-center gap-6 pt-8 border-t border-white/10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex-1">
              <p className="text-slate-300 text-sm md:text-base">
                Have a property that needs the same kind of transformation? Let&apos;s talk.
              </p>
            </div>
            <Link to="/contact" data-testid="gateway-cta-btn">
              <Button 
                className="bg-ktm-red hover:bg-ktm-red/90 text-white rounded-none font-bold uppercase tracking-wider px-8 py-6"
              >
                Start Your Project
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-ktm-muted" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-ktm-red font-bold uppercase tracking-wider text-sm">
              Why KTM Construction
            </span>
            <h2 className="font-chivo font-black text-3xl md:text-4xl text-ktm-navy mt-4">
              Built on Trust
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-20 h-20 bg-ktm-navy mx-auto flex items-center justify-center mb-6">
                  <value.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-chivo font-bold text-xl text-ktm-navy mb-4">
                  {value.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-ktm-red" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-chivo font-black text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Contact us today for a free consultation and quote. 
              Let&apos;s build something amazing together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?quote=true">
                <Button 
                  className="bg-white text-ktm-navy h-14 px-8 rounded-none font-bold uppercase tracking-wider hover:bg-slate-100 w-full sm:w-auto"
                  data-testid="cta-quote-btn"
                >
                  Get Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:916-872-1882">
                <Button 
                  variant="outline"
                  className="bg-transparent text-white border-2 border-white h-14 px-8 rounded-none font-bold uppercase tracking-wider hover:bg-white/10 w-full sm:w-auto"
                  data-testid="cta-call-btn"
                >
                  Call 916-872-1882
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
